/**
 * Slash Command Parser
 *
 * Parses chat input for slash commands and routes them appropriately.
 * Phase 1 commands are handled client-side for instant response.
 * Phase 2 commands interact with conversation data via API.
 * Phase 3 commands use light parsing for quick object creation.
 */

export type CommandName =
  | 'help'
  | 'clear'
  | 'new'
  | 'feedback'
  | 'conversation'
  | 'conversations'
  | 'search'
  | 'task'
  | 'note'
  | 'contact'
  | 'event'
  // Card views — render the Today card directly (no agent round-trip)
  | 'schedule'
  | 'today' // legacy alias for schedule
  | 'travel'
  | 'food'
  | 'circle'
  | 'profile'
  | 'week'
  // Agent commands — send a synthesized prompt to the agent
  | 'social'
  | 'inbox'
  | 'draft'
  | 'find'
  | 'report'
  | 'who'
  | 'prep'
  | 'brief'
  // Module-opener agent commands
  | 'contacts'
  | 'places'
  | 'tasks'
  | 'pages'
  | 'drive';

export interface ParsedCommand {
  type: 'command';
  command: CommandName;
  args: string;
}

export interface ParsedMessage {
  type: 'message';
  content: string;
}

export type ParseResult = ParsedCommand | ParsedMessage;

const KNOWN_COMMANDS: CommandName[] = [
  'help',
  'clear',
  'new',
  'feedback',
  'conversation',
  'conversations',
  'search',
  'task',
  'note',
  'contact',
  'event',
  'schedule',
  'today',
  'travel',
  'food',
  'circle',
  'profile',
  'week',
  'social',
  'inbox',
  'draft',
  'find',
  'report',
  'who',
  'prep',
  'brief',
  'contacts',
  'places',
  'tasks',
  'pages',
  'drive',
];

const COMMAND_ALIASES: Record<string, CommandName> = {
  today: 'schedule', // /today → /schedule (legacy alias)
  studio: 'pages',  // /studio → /pages (Pages module is branded Studio)
};

/**
 * Parse input to detect slash commands
 *
 * Rules:
 * - Commands start with / followed by a word
 * - // escapes to literal / (sends "/text" as message)
 * - Quoted messages bypass command parsing
 */
export function parseInput(input: string): ParseResult {
  const trimmed = input.trim();

  if (!trimmed) {
    return { type: 'message', content: '' };
  }

  // Escape: // at start sends literal /
  if (trimmed.startsWith('//')) {
    return { type: 'message', content: trimmed.slice(1) };
  }

  // Quoted messages bypass command parsing
  if (trimmed.startsWith('"') && trimmed.endsWith('"') && trimmed.length > 2) {
    return { type: 'message', content: trimmed.slice(1, -1) };
  }

  // Check for command pattern: /word (alphanumeric so "/10 days" / "/30days" work)
  const commandMatch = trimmed.match(/^\/([a-zA-Z0-9]+)(?:\s+(.*))?$/);
  if (!commandMatch) {
    return { type: 'message', content: trimmed };
  }

  const commandName = commandMatch[1].toLowerCase();
  const args = commandMatch[2]?.trim() || '';
  const canonicalName = COMMAND_ALIASES[commandName] ?? commandName;

  if (KNOWN_COMMANDS.includes(canonicalName as CommandName)) {
    return {
      type: 'command',
      command: canonicalName as CommandName,
      args,
    };
  }

  // Unknown command - treat as regular message for now
  // Future: could show error or pass to agent
  return { type: 'message', content: trimmed };
}

export function isKnownCommand(command: string): boolean {
  const canonicalName = COMMAND_ALIASES[command.toLowerCase()] ?? command.toLowerCase();
  return KNOWN_COMMANDS.includes(canonicalName as CommandName);
}

export function getHelpText(): string {
  return `**Available Commands**

**Basic**
- \`/help\` - Show this help message
- \`/clear\` - Clear chat history
- \`/new\` - Start a new conversation
- \`/feedback\` - Open feedback form

**Conversation & Search**
- \`/conversation\` - Show current conversation info
- \`/conversations\` - List all your conversations
- \`/search <query>\` - Search within current conversation

**Quick Create**
- \`/task <title>\` - Create a task (supports "tomorrow", "next week", etc.)
- \`/note <title>\` - Create a note
- \`/contact <name>\` - Create a contact (auto-detects email/phone)
- \`/event <title>\` - Create an event (supports date/time parsing)

**Cards**
- \`/schedule\` - Today's schedule + tasks
- \`/week\` - Curated key events, next 10 days

*Tip: Type naturally to chat with the AI, or use commands for quick actions.*`;
}

export interface CommandInfo {
  name: CommandName;
  description: string;
  /** Whether this command takes arguments (e.g., /search <query>) */
  hasArgs?: boolean;
}

export const COMMANDS: CommandInfo[] = [
  // Most useful lead the menu
  { name: 'schedule', description: 'Your day and the next 90 days at a glance' },
  { name: 'circle', description: 'Your inner circle — closest people + who is going quiet' },
  { name: 'travel', description: 'Your travel report — 12 months of trips from your calendar' },
  { name: 'food', description: 'Where you eat — your reservations report' },
  { name: 'social', description: 'Your social presence — X, LinkedIn, and what the web knows about you' },
  { name: 'profile', description: 'Your card — the shareable archetype profile' },

  { name: 'inbox', description: 'Triage your inbox — what needs attention' },
  { name: 'draft', description: 'Write an email in your voice', hasArgs: true },
  { name: 'find', description: 'Find anyone or anything in your data', hasArgs: true },
  { name: 'contacts', description: 'Find, import, and manage your people' },
  { name: 'places', description: 'Restaurants, hotels, places you\'ve been' },
  { name: 'tasks', description: 'Create, track, and move your tasks' },
  { name: 'pages', description: 'Studio — instant, shareable HTML reports and decks' },
  { name: 'drive', description: 'Store, read, and write files' },
  { name: 'report', description: 'Build a report — /report <topic> (default: about me)', hasArgs: true },
  { name: 'who', description: 'Look up a person — /who <name> (no name: top contacts)', hasArgs: true },
  { name: 'prep', description: 'Prep me — next meeting or trip', hasArgs: true },
  { name: 'brief', description: 'What moved overnight — mail + calendar' },
  { name: 'week', description: 'Key events, next 10 days' },
  // Remaining commands
  { name: 'help', description: 'Show available commands' },
  { name: 'clear', description: 'Clear chat history' },
  { name: 'new', description: 'Start a new conversation' },
  { name: 'feedback', description: 'Open feedback form' },
  { name: 'conversation', description: 'Show current conversation info' },
  { name: 'conversations', description: 'List all your conversations' },
  { name: 'search', description: 'Search within current conversation', hasArgs: true },
  { name: 'task', description: 'Create a task', hasArgs: true },
  { name: 'note', description: 'Create a note', hasArgs: true },
  { name: 'contact', description: 'Create a contact', hasArgs: true },
  { name: 'event', description: 'Create an event', hasArgs: true },
];

/**
 * Horizon the card-view commands open on. Single source of truth — consumed by
 * useChatSubmit when injecting the Today card; null for non-card commands.
 */
export function cardHorizonFor(command: CommandName): 'today' | '10' | null {
  switch (command) {
    case 'schedule':
    case 'today': return 'today';
    case 'week': return '10';
    default: return null;
  }
}

export function getMatchingCommands(prefix: string): CommandInfo[] {
  const lower = prefix.toLowerCase();
  return COMMANDS.filter(cmd => cmd.name.startsWith(lower));
}
